/**
 * Offentlige under-sider for en regatta: påmeldte (/r/:slug/pameldte) og
 * resultater (/r/:slug/resultater). Lenkes fra regattalista (/regattaer).
 */
import type { ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Race } from '../lib/api.ts';
import { SiteLayout, SAND } from '../components/SiteLayout.tsx';
import { formatElapsed } from '../components/FinishTiming.tsx';

function Status({ text }: { text: string }) {
  return (
    <SiteLayout>
      <div className="py-24 text-center text-slate-400">{text}</div>
    </SiteLayout>
  );
}

function RaceSubPage({
  race,
  slug,
  active,
  children,
}: {
  race: Race;
  slug: string;
  active: 'resultater' | 'pameldte';
  children: ReactNode;
}) {
  const finished = race.status === 'finished';
  const date = new Date(race.startTime).toLocaleString('no-NO', {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZone: 'Europe/Oslo',
  });
  const tab = (key: 'resultater' | 'pameldte', label: string) => (
    <Link
      to={`/r/${slug}/${key}`}
      className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
        active === key ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
      }`}
    >
      {label}
    </Link>
  );

  return (
    <SiteLayout>
      <main className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/regattaer" className="text-sm text-slate-400 hover:text-slate-200">
          ← Regattaer
        </Link>
        <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-white">{race.name}</h1>
        <p className="mt-1 text-sm text-slate-400">
          {date}
          {race.venue ? ` · ${race.venue}` : ''}
          {race.status === 'cancelled' ? ' · Avlyst' : ''}
        </p>
        <nav className="mt-5 flex flex-wrap items-center gap-1 border-b border-slate-800 pb-3">
          {tab('resultater', '🏁 Resultater')}
          {tab('pameldte', '⛵ Påmeldte')}
          <a
            href={finished ? `/kart/${slug}/reprise` : `/kart/${slug}`}
            target="rc-kart"
            className="px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-white"
          >
            {finished ? '⏮ Reprise' : '🗺️ Følg live'}
          </a>
          <Link to={`/r/${slug}`} className="px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-white">
            ⏱ Regattaside
          </Link>
        </nav>
        <div className="mt-5">{children}</div>
      </main>
    </SiteLayout>
  );
}

export function RaceEntriesPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ['race-entries', slug],
    queryFn: () => api.race(slug!),
    enabled: !!slug,
  });

  if (isLoading) return <Status text="Laster…" />;
  if (!data) return <Status text="Klarte ikke å laste regattaen." />;

  return (
    <RaceSubPage race={data.race} slug={slug!} active="pameldte">
      <h2 className="text-xs font-bold tracking-[0.14em] uppercase mb-3" style={{ color: SAND }}>
        Påmeldte ({data.entries.length})
      </h2>
      {data.entries.length === 0 ? (
        <p className="text-sm text-slate-400">Ingen påmeldte ennå.</p>
      ) : (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {data.entries.map((e) => (
            <li key={e.id} className="p-3 bg-slate-900/40 flex items-center gap-3">
              <div className="font-mono text-xs text-slate-400 w-16 shrink-0">{e.sailNumber}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{e.boatName}</div>
                <div className="text-xs text-slate-400 truncate">
                  Skipper: {e.skipperName}
                  {e.crew && ` · Mannskap: ${e.crew}`}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </RaceSubPage>
  );
}

/** Plassering 1–3 fremheves. */
function placeCls(position: number | null) {
  if (position === 1) return 'text-white';
  if (position === 2) return 'bg-slate-300 text-slate-900';
  if (position === 3) return 'bg-amber-700 text-white';
  if (position == null) return 'bg-slate-900 text-slate-500 border border-slate-800';
  return 'bg-slate-800 text-slate-200';
}

/** Resultater = den offisielle målgangslista (finish_records), slik komiteen har ført den. */
export function RaceResultsPage() {
  const { slug } = useParams<{ slug: string }>();
  const race = useQuery({
    queryKey: ['race-entries', slug],
    queryFn: () => api.race(slug!),
    enabled: !!slug,
  });
  const finish = useQuery({
    queryKey: ['race-finish', slug],
    queryFn: () => api.finishList(slug!),
    enabled: !!slug,
    refetchInterval: 10_000,
  });

  if (race.isLoading || finish.isLoading) return <Status text="Laster…" />;
  if (!race.data || !finish.data) return <Status text="Klarte ikke å laste regattaen." />;

  const done = race.data.race.status === 'finished';
  const records = finish.data.records;
  // Lista kan være forhåndsutfylt med seilnummer uten at plass/tid er ført.
  const anyResult = records.some((f) => f.position != null || f.elapsedMs != null || !!f.note);

  return (
    <RaceSubPage race={race.data.race} slug={slug!} active="resultater">
      <h2 className="text-xs font-bold tracking-[0.14em] uppercase mb-3" style={{ color: SAND }}>
        {done ? 'Resultater' : 'Målgang så langt'}
      </h2>
      {records.length > 0 && !anyResult && (
        <p className="mb-4 text-sm text-slate-400">
          {done
            ? 'Plassering og tid er ikke ført for denne regattaen.'
            : 'Ingen båter er registrert i mål ennå.'}
        </p>
      )}
      {records.length === 0 ? (
        <p className="text-sm text-slate-400">
          {done
            ? 'Ingen målgang er registrert for denne regattaen.'
            : 'Resultatene kommer når båtene går i mål.'}
        </p>
      ) : (
        <ol className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {records.map((f) => (
            <li key={f.id} className="p-3 bg-slate-900/40 flex items-center gap-3">
              <div
                className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-bold tabular-nums ${placeCls(
                  f.position,
                )}`}
                style={f.position === 1 ? { background: SAND } : undefined}
              >
                {f.position ?? '–'}
              </div>
              <div className="font-mono text-xs text-slate-400 w-16 shrink-0">{f.sailNumber ?? '—'}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{f.boatName ?? f.sailNumber ?? 'Ukjent båt'}</div>
                {f.skipperName && <div className="text-xs text-slate-400 truncate">{f.skipperName}</div>}
              </div>
              <div className="shrink-0 text-right">
                {f.elapsedMs != null && (
                  <div className="font-mono tabular-nums text-sm text-slate-100">
                    {formatElapsed(f.elapsedMs)}
                  </div>
                )}
                {f.note && <div className="text-xs font-semibold text-amber-300">{f.note}</div>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </RaceSubPage>
  );
}
